import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Beef,
  ChefHat,
  Clock,
  Croissant,
  Drumstick,
  Flame,
  Leaf,
  Moon,
  Salad,
  Soup,
  Sun,
  UtensilsCrossed,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import {
  AnalyticsExportButtons,
  AnalyticsFilters,
  ChartFrame,
  DataTable,
  HourDayHeatmap,
  KpiCard,
  MetricBars,
  Panel,
  PIE_TONES,
  TONE_VAR,
  inr,
  num,
  pct0,
  tooltipStyle,
} from "@/components/admin/analytics/AnalyticsKit";
import { getFoodAnalytics } from "@/lib/admin/analytics-suite.functions";
import type { SuiteRangeKey } from "@/lib/admin/analytics-range";
import type { FoodAnalytics } from "@/lib/admin/food-analytics.data";

export const Route = createFileRoute("/_admin/admin/food-analytics")({
  component: FoodAnalyticsPage,
});

function FoodAnalyticsPage() {
  const [range, setRange] = useState<SuiteRangeKey>("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const fetchFood = useServerFn(getFoodAnalytics);

  const q = useQuery({
    queryKey: ["admin", "food-analytics", range, from, to],
    queryFn: () => fetchFood({ data: { range, ...(range === "custom" ? { from, to } : {}) } }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const d = q.data as FoodAnalytics | undefined;
  const loading = q.isLoading;
  const k = d?.kpis;
  const unverified = new Set(
    (d as { unverifiedKpis?: string[] } | undefined)?.unverifiedKpis ?? [],
  );
  const nm = (key: string, val: string) => (unverified.has(key) ? "Not measured" : val);

  const exportRows = useMemo(() =>
    (d?.dishes ?? []).map((r) => ({
      Dish: r.name,
      Category: r.category,
      Orders: r.orders,
      "Revenue (₹)": r.revenue,
      "Returns (₹)": "Not measured",
      Rating: "Not measured",
    })), [d?.dishes]);

  const { onExportCsv, onExportExcel } = AnalyticsExportButtons({
    name: "food-analytics",
    rows: exportRows,
  });

  return (
    <div>
      <AdminPageHeader
        title="Food Analytics"
        description="Dish demand, category mix, nutrition profile and kitchen efficiency."
      />

      <RefreshBar
        targets={[{ key: "food-analytics", label: "food" }]}
        busy={q.isFetching}
        lastUpdated={q.dataUpdatedAt}
      />

      <AnalyticsFilters
        range={range}
        onRangeChange={setRange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        status={d ? `${d.rangeLabel} · ${(d as { source?: string }).source === "live" ? "Live" : "Not measured"}` : "Loading…"}
        onExportCsv={onExportCsv}
        onExportExcel={onExportExcel}
      />

      {(d as { source?: string } | undefined)?.source !== "live" ? (
        <div role="status" className="rounded-lg border border-border bg-muted p-6 text-sm text-muted-foreground">
          {q.isError ? "Unavailable — the analytics lookup failed." : "Not measured — Food Analytics is not yet connected to verified live production data. No figures are shown to avoid displaying demo or misleading values."}
        </div>
      ) : null}
      {(d as { source?: string } | undefined)?.source === "live" ? (<>

      <section aria-label="Food key metrics" className="mb-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          <KpiCard
            label="Meals ordered"
            value={num(k?.mealsSold ?? 0)}
            tone="warn"
            icon={UtensilsCrossed}
            delta={k?.deltas.mealsSold}
            loading={loading}
            to="/admin/kitchen-production"
          />
          <KpiCard
            label="Meal plans ordered"
            value={num(k?.mealPlansSold ?? 0)}
            tone="warn"
            icon={ChefHat}
            delta={k?.deltas.mealPlansSold}
            loading={loading}
            to="/admin/meal-plans-v2"
          />
          <KpiCard
            label="Most ordered dish"
            value={k?.mostOrderedDish ?? "—"}
            tone="success"
            icon={Flame}
            loading={loading}
            hint="Highest order count"
          />
          <KpiCard
            label="Least ordered dish"
            value={k?.leastOrderedDish ?? "—"}
            tone="danger"
            icon={Soup}
            loading={loading}
            hint="Review or retire"
          />
          <KpiCard
            label="Avg calories sold"
            value={nm("avgCalories", `${num(k?.avgCalories ?? 0)} kcal`)}
            tone="purple"
            icon={Flame}
            delta={k?.deltas.avgCalories}
            loading={loading}
          />
          <KpiCard
            label="Veg share"
            value={nm("vegShare", pct0(k?.vegShare ?? 0))}
            tone="success"
            icon={Leaf}
            delta={k?.deltas.vegShare}
            loading={loading}
          />
          <KpiCard
            label="Non veg share"
            value={nm("nonVegShare", pct0(k?.nonVegShare ?? 0))}
            tone="danger"
            icon={Drumstick}
            delta={k?.deltas.nonVegShare}
            loading={loading}
          />
          <KpiCard
            label="Average protein"
            value={nm("avgProtein", `${num(k?.avgProtein ?? 0)} g`)}
            tone="teal"
            icon={Beef}
            delta={k?.deltas.avgProtein}
            loading={loading}
          />
          <KpiCard
            label="Popular breakfast"
            value={k?.popularBreakfast ?? "—"}
            tone="info"
            icon={Croissant}
            loading={loading}
          />
          <KpiCard
            label="Popular lunch"
            value={k?.popularLunch ?? "—"}
            tone="warn"
            icon={Sun}
            loading={loading}
          />
          <KpiCard
            label="Popular snacks"
            value={k?.popularSnack ?? "—"}
            tone="teal"
            icon={Salad}
            loading={loading}
          />
          <KpiCard
            label="Popular dinner"
            value={k?.popularDinner ?? "—"}
            tone="purple"
            icon={Moon}
            loading={loading}
          />
        </div>
      </section>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Best selling dishes" subtitle="Orders in period" className="xl:col-span-2">
          <ChartFrame loading={loading} empty={(d?.bestSellers.length ?? 0) === 0} height="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.bestSellers ?? []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tick={{ fontSize: 11 }}
                  stroke="var(--color-muted-foreground)"
                />
                <Tooltip {...tooltipStyle()} />
                <Bar dataKey="value" name="Orders" fill={TONE_VAR.warn} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Worst selling dishes" subtitle="Lowest demand">
          <MetricBars rows={d?.worstSellers ?? []} tone="danger" loading={loading} />
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Category performance" subtitle="Meals by category">
          <ChartFrame loading={loading} empty={(d?.categories.length ?? 0) === 0} height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={d?.categories ?? []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {(d?.categories ?? []).map((s, i) => (
                    <Cell key={s.name} fill={TONE_VAR[PIE_TONES[i % PIE_TONES.length]!]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Veg vs non veg" subtitle="Share of meals">
          <ChartFrame loading={loading} empty={(d?.vegSplit.length ?? 0) === 0} height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={d?.vegSplit ?? []} dataKey="value" nameKey="name" outerRadius={85}>
                  <Cell fill={TONE_VAR.success} />
                  <Cell fill={TONE_VAR.danger} />
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Meal preference" subtitle="Veg · Vegan · Eggetarian · Non veg">
          <MetricBars rows={d?.preferences ?? []} tone="success" loading={loading} />
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Protein rich orders & calories" subtitle="Daily nutrition profile">
          <ChartFrame loading={loading} empty={(d?.proteinTrend.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.proteinTrend ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="proteinOrders"
                  name="Protein-rich orders"
                  stroke={TONE_VAR.teal}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="calories"
                  name="Avg calories"
                  stroke={TONE_VAR.purple}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="protein"
                  name="Avg protein (g)"
                  stroke={TONE_VAR.warn}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Popular add-ons" subtitle="Attached to meals">
          <MetricBars rows={d?.addons ?? []} tone="warn" loading={loading} />
        </Panel>
      </div>

      <Panel
        title="Frequently bought together"
        subtitle="Strongest dish pairings"
        className="mb-6"
      >
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {(d?.pairs ?? []).map((p) => (
            <li
              key={`${p.a}-${p.b}`}
              className="rounded-lg border border-border p-3 transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="truncate font-medium text-foreground">{p.a}</span>
                <span
                  className="h-px flex-1"
                  style={{ background: TONE_VAR.warn }}
                  aria-hidden
                />
                <span className="truncate font-medium text-foreground">{p.b}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {num(p.value)} orders contained both
              </p>
            </li>
          ))}
        </ul>
        {loading ? <p className="text-xs text-muted-foreground">Loading…</p> : null}
      </Panel>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Meal plan performance">
          <DataTable
            rows={d?.plans ?? []}
            loading={loading}
            exportName="meal-plan-performance"
            searchOn={(r) => r.name}
            minWidth={520}
            columns={[
              {
                key: "name",
                header: "Plan",
                render: (r) => (
                  <div className="flex items-center gap-2">
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[10px] font-semibold"
                      style={{ background: "var(--ops-warn-soft)", color: "var(--ops-warn)" }}
                      aria-hidden
                    >
                      {r.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="truncate font-medium text-foreground">{r.name}</span>
                  </div>
                ),
                sortValue: (r) => r.name,
                exportValue: (r) => r.name,
              },
              {
                key: "orders",
                header: "Orders",
                align: "right",
                render: (r) => num(r.orders),
                sortValue: (r) => r.orders,
                exportValue: (r) => r.orders,
              },
              {
                key: "revenue",
                header: "Revenue",
                align: "right",
                render: (r) => inr(r.revenue),
                sortValue: (r) => r.revenue,
                exportValue: (r) => r.revenue,
              },
              {
                key: "rating",
                header: "Rating",
                align: "right",
                render: (r) => `${r.rating} ★`,
                sortValue: (r) => r.rating,
                exportValue: (r) => r.rating,
              },
            ]}
          />
        </Panel>

        <Panel title="Dish performance">
          <DataTable
            rows={d?.dishes ?? []}
            loading={loading}
            exportName="dish-performance"
            searchOn={(r) => `${r.name} ${r.category}`}
            minWidth={560}
            columns={[
              {
                key: "name",
                header: "Dish",
                render: (r) => (
                  <div className="flex items-center gap-2">
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[10px] font-semibold"
                      style={{ background: "var(--ops-teal-soft)", color: "var(--ops-teal)" }}
                      aria-hidden
                    >
                      {r.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-foreground">{r.name}</span>
                      <span className="block text-xs text-muted-foreground">{r.category}</span>
                    </span>
                  </div>
                ),
                sortValue: (r) => r.name,
                exportValue: (r) => r.name,
              },
              {
                key: "orders",
                header: "Orders",
                align: "right",
                render: (r) => num(r.orders),
                sortValue: (r) => r.orders,
                exportValue: (r) => r.orders,
              },
              {
                key: "revenue",
                header: "Revenue",
                align: "right",
                render: (r) => inr(r.revenue),
                sortValue: (r) => r.revenue,
                exportValue: (r) => r.revenue,
              },
              {
                key: "returns",
                header: "Returns",
                align: "right",
                render: () => "Not measured",
                exportValue: () => "Not measured",
              },
            ]}
          />
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Out of stock frequency" subtitle="Times marked unavailable">
          <MetricBars rows={d?.outOfStock ?? []} tone="danger" loading={loading} />
        </Panel>

        <Panel title="Preparation time analysis" subtitle="Minutes vs target">
          <ChartFrame loading={loading} empty={(d?.prepTime.length ?? 0) === 0} height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.prepTime ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="minutes" name="Actual" fill={TONE_VAR.warn} radius={[3, 3, 0, 0]} />
                <Bar dataKey="target" name="Target" fill={TONE_VAR.neutral} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Kitchen efficiency" subtitle="On-time % and prep minutes">
          <ChartFrame
            loading={loading}
            empty={(d?.kitchenEfficiency.length ?? 0) === 0}
            height="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.kitchenEfficiency ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="avgPrepMin"
                  name="Avg prep (min)"
                  stroke={TONE_VAR.info}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
          <p className="mt-2 text-xs text-muted-foreground">
            Food waste{" "}
            <span className="font-medium text-foreground">{pct0(d?.wastePct ?? 0)}</span> of
            produced meals · <ChefHat className="inline h-3 w-3" aria-hidden /> target under 3%.
          </p>
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Peak ordering time" subtitle="Hour vs day of week">
          <HourDayHeatmap rows={d?.peakHeat ?? []} tone="warn" loading={loading} />
        </Panel>

        <Panel title="Area wise food preference" subtitle="Meals by diet per neighbourhood">
          <ChartFrame loading={loading} empty={(d?.areaPreference.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.areaPreference ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="area"
                  tick={{ fontSize: 10 }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                  stroke="var(--color-muted-foreground)"
                />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="veg" name="Veg" stackId="a" fill={TONE_VAR.success} />
                <Bar dataKey="vegan" name="Vegan" stackId="a" fill={TONE_VAR.teal} />
                <Bar dataKey="nonVeg" name="Non veg" stackId="a" fill={TONE_VAR.danger} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" aria-hidden /> Updated {d ? d.rangeLabel : "—"}
          </p>
        </Panel>
      </div>
      </>) : null}
    </div>
  );
}
